"use client";

import { useState, useMemo, useCallback } from "react";
import { Copy, Users, MessageCircle, MoreHorizontal } from "lucide-react";
import Image from "next/image";
import { Button } from "@heroui/react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useGetCampaignUser } from "@/hooks/challenges";
import { useGetReferrals } from "@/hooks/useReferrals";
import { useGetCampaign } from "@/hooks/challenges";
import { useConnection } from "@/contexts/connection-context";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";

export default function ReferralsPage() {
  const { isAuthenticated, walletAddress } = useConnection();
  const { data: campaignUser, isLoading: userLoading } = useGetCampaignUser(CAMPAIGN_ID, CAMPAIGN_GROUP_ID, isAuthenticated);
  const { data: campaign, isLoading: campaignLoading } = useGetCampaign(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);
  
  // Pagination state for referred users
  const [referralsPage, setReferralsPage] = useState(1);
  const referralsLimit = 10;
  
  // Get referred users data
  const { 
    data: referralsData, 
    isLoading: referralsLoading, 
    isError: referralsError 
  } = useGetReferrals(
    CAMPAIGN_ID, 
    CAMPAIGN_GROUP_ID, 
    walletAddress || "", 
    referralsPage, 
    referralsLimit
  );
  
  // Get referral code from real data
  const referralCode = useMemo(() => {
    return campaignUser?.referralData?.code || "";
  }, [campaignUser?.referralData?.code]);
  
  // Generate full referral link for copying/sharing
  const referralLink = useMemo(() => {
    if (!referralCode || !walletAddress) {
      return "";
    }
    
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://rhuna.app";
    return `${baseUrl}/?referralCode=${referralCode}&address=${walletAddress}`;
  }, [referralCode, walletAddress]);
  
  // Extract real stats from referrals data
  const friendsReferred = useMemo(() => {
    return referralsData?.count || 0;
  }, [referralsData?.count]);
  
  const pointsEarned = useMemo(() => {
    return campaignUser?.referralData?.pointsObtained || "0";
  }, [campaignUser?.referralData?.pointsObtained]);

  // Function to calculate points earned from a referral based on their points and referral tiers
  const calculatePointsEarnedFromReferral = useCallback((userPoints: string) => {
    if (!campaign?.metadata?.referralTiers) return { pointsEarned: 0, hasReachedThreshold: false, tierReached: null };
    
    const points = parseFloat(userPoints) || 0;
    const tiers = campaign.metadata.referralTiers;
    
    // Sort tiers by threshold (ascending)
    const sortedTiers = Object.entries(tiers)
      .map(([threshold, reward]) => ({
        threshold: parseFloat(threshold),
        reward: parseFloat(reward)
      }))
      .sort((a, b) => b.threshold - a.threshold); // Sort descending to find highest tier reached
    
    // Find the highest tier the user has reached
    const tierReached = sortedTiers.find(tier => points >= tier.threshold);
    
    return {
      pointsEarned: tierReached ? tierReached.reward : 0,
      hasReachedThreshold: !!tierReached,
      tierReached: tierReached
    };
  }, [campaign?.metadata?.referralTiers]);
  
  const handleCopyLink = () => {
    if (!referralCode) {
      toast.error("Referral code not available. Please connect your wallet.");
      return;
    }
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied to clipboard!");
  };

  const handleSocialShare = (platform: string) => {
    if (!referralCode) {
      toast.error("Referral code not available. Please connect your wallet.");
      return;
    }
    
    const shareText = `Join me on RHUNA and earn rewards! Use my referral link: ${referralLink}`;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'discord':
        // For Discord, we'll just copy the link with a message
        navigator.clipboard.writeText(`${shareText}`);
        toast.success("Discord message copied to clipboard!");
        break;
      case 'more':
        // Native share API if available
        if (navigator.share) {
          navigator.share({
            title: 'Join RHUNA',
            text: shareText,
            url: referralLink
          });
        } else {
          handleCopyLink();
        }
        break;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0C0F] text-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
        {/* Header Section */}
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Referral Program</h1>
          <p className="text-[#99A1AF] text-base sm:text-lg">Invite friends and earn rewards together</p>
        </div>

        {/* Hero Section - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left: Friends Referred Card with Background (1/3) */}
          <div className="relative bg-[#0C1113] border border-gray-800/50 rounded-lg p-6 overflow-hidden">
            {/* Background Image */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: 'url(https://pulsarmoney.b-cdn.net/rhuna/referral_bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            
            {/* Content */}
            <div className="relative z-10 flex flex-col h-full">
              {/* Icon - Top Left */}
              <div className="bg-[#60FF8A]/10 p-2 sm:p-3 rounded-lg w-fit mb-auto">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#60FF8A]" />
              </div>
              
              {/* Text - Bottom Left */}
              <div className="mt-auto">
                <div className="text-4xl sm:text-6xl font-bold text-[#60FF8A] mb-2">
                  {userLoading ? "..." : friendsReferred}
                </div>
                <div className="text-[#99A1AF] text-base sm:text-lg">Friends Referred</div>
              </div>
            </div>
          </div>

          {/* Right: Your Referral Link Section (2/3) */}
          <div className="lg:col-span-2 bg-[#0C1113] border border-gray-800/50 rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white">Your Referral Link</h2>
            
            <div className="bg-[#0A0C0F] border border-gray-800/50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <input
                  type="text"
                  value={referralCode || (userLoading ? "Loading..." : "Connect wallet to get referral code")}
                  readOnly
                  className="flex-1 bg-transparent text-white placeholder-[#99A1AF] outline-none text-xs sm:text-sm font-mono px-2 py-2 border border-gray-700/50 rounded"
                  placeholder="Connect wallet to get referral code"
                />
                <Button
                  onClick={handleCopyLink}
                  isDisabled={!referralCode}
                  className="bg-[#60FF8A] text-black font-semibold px-4 py-3 rounded-lg hover:bg-[#60FF8A]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center w-full sm:w-auto"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>

            {/* Social Share Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button
                onClick={() => handleSocialShare('twitter')}
                isDisabled={!referralCode}
                className="flex-1 bg-black text-white font-semibold px-6 py-3 rounded-lg hover:bg-black/90 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed border border-white"
              >
                <Image
                  src="https://pulsarmoney.b-cdn.net/rhuna/x.png"
                  alt="X"
                  width={20}
                  height={20}
                  className="w-5 h-5"
                />
              </Button>
              
              <Button
                onClick={() => handleSocialShare('discord')}
                isDisabled={!referralCode}
                className="flex-1 bg-[#5865F2] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#5865F2]/90 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Image
                  src="https://pulsarmoney.b-cdn.net/rhuna/discord_icon_new.png"
                  alt="Discord"
                  width={20}
                  height={20}
                  className="w-5 h-5"
                />
              </Button>
              
              <Button
                onClick={() => handleSocialShare('more')}
                isDisabled={!referralCode}
                className="flex-1 bg-[#374151] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#374151]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MoreHorizontal className="w-5 h-5" />
                More
              </Button>
            </div>
          </div>
        </div>

        {/* Points Earned Section */}
        <div className="bg-[#0C1113] border border-gray-800/50 rounded-lg p-6">
          <div className="flex justify-between items-center">
            <span className="text-white font-semibold text-lg">Points Earned</span>
            <span className="text-[#60FF8A] font-bold text-xl">
              {userLoading ? "..." : `+${pointsEarned} RP`}
            </span>
          </div>
        </div>

        {/* Referral Tiers Info */}
        {campaign?.metadata?.referralTiers && (
          <div className="bg-[#0C1113] border border-gray-800/50 rounded-lg p-6">
            <h3 className="text-white font-semibold text-lg mb-4">How Referrals Work</h3>
            <div className="space-y-3">
              <p className="text-gray-400 text-sm">
                Earn points when your referred users reach certain milestones:
              </p>
              <div className="space-y-2">
                {Object.entries(campaign.metadata.referralTiers)
                  .map(([threshold, reward]) => ({
                    threshold: parseFloat(threshold),
                    reward: parseFloat(reward)
                  }))
                  .sort((a, b) => a.threshold - b.threshold)
                  .map((tier, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-800/30 rounded">
                      <span className="text-white text-sm">
                        Friend reaches {tier.threshold}+ points
                      </span>
                      <span className="text-[#60FF8A] font-semibold">
                        +{tier.reward} RP for you
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Referred Users List */}
        <div className="bg-[#0C1113] border border-gray-800/50 rounded-lg p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-semibold text-lg">Referred Users</h3>
            <span className="text-gray-400 text-sm">
              {referralsLoading ? "..." : `${friendsReferred} total`}
            </span>
          </div>

          {referralsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          ) : referralsError ? (
            <div className="text-center py-8">
              <p className="text-red-400">Failed to load referred users</p>
            </div>
          ) : !referralsData?.data || referralsData.data.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No referred users yet</p>
              <p className="text-gray-500 text-sm mt-2">Share your referral code to start earning!</p>
            </div>
          ) : (
            <div>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-3 bg-gray-800/30 rounded-lg mb-3 text-sm font-medium text-gray-300">
                  <div className="col-span-3">Referral</div>
                  <div className="col-span-3">Address</div>
                  <div className="col-span-2 text-center">Their Points</div>
                  <div className="col-span-2 text-center">Status</div>
                  <div className="col-span-2 text-center">You Earned</div>
                </div>

                {/* Table Rows */}
                <div className="space-y-2">
                  {referralsData.data.map((user, index) => {
                    const userPoints = parseFloat(user.totalPoints || "0");
                    const { pointsEarned, hasReachedThreshold, tierReached } = calculatePointsEarnedFromReferral(user.totalPoints || "0");
                    
                    return (
                      <div key={user.address} className="grid grid-cols-12 gap-4 p-3 bg-gray-800/30 rounded-lg hover:bg-gray-700/30 transition-colors">
                        {/* Referral Number */}
                        <div className="col-span-3 flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#60FF8A] rounded-full flex items-center justify-center text-black font-bold text-sm">
                            {index + 1}
                          </div>
                          <span className="text-white font-medium">
                            Referral {index + 1}
                          </span>
                        </div>

                        {/* Address */}
                        <div className="col-span-3 flex items-center">
                          <span className="text-gray-400 text-sm font-mono">
                            {user.address.slice(0, 6)}...{user.address.slice(-4)}
                          </span>
                        </div>

                        {/* Their Points */}
                        <div className="col-span-2 text-center flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {userPoints.toFixed(0)} RP
                          </span>
                        </div>

                        {/* Status */}
                        <div className="col-span-2 text-center flex items-center justify-center">
                          {hasReachedThreshold ? (
                            <div className="flex items-center gap-1 text-[#60FF8A]">
                              <div className="w-2 h-2 bg-[#60FF8A] rounded-full"></div>
                              <span className="text-xs font-medium">Threshold Reached</span>
                            </div>
                          ) : userPoints > 0 ? (
                            <div className="flex items-center gap-1 text-gray-500">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                              <span className="text-xs">Not Yet</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-gray-600">
                              <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
                              <span className="text-xs">No Activity</span>
                            </div>
                          )}
                        </div>

                        {/* You Earned */}
                        <div className="col-span-2 text-center flex items-center justify-center">
                          {pointsEarned > 0 ? (
                            <div className="flex items-center gap-1 text-[#60FF8A]">
                              <span className="font-bold text-sm">+{pointsEarned}</span>
                              <span className="text-xs">RP</span>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-xs">-</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-3">
                {referralsData.data.map((user, index) => {
                  const userPoints = parseFloat(user.totalPoints || "0");
                  const { pointsEarned, hasReachedThreshold, tierReached } = calculatePointsEarnedFromReferral(user.totalPoints || "0");
                  
                  return (
                    <div key={user.address} className="bg-gray-800/30 rounded-lg p-4 space-y-3">
                      {/* Top Row: Referral Number and Status */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#60FF8A] rounded-full flex items-center justify-center text-black font-bold text-sm">
                            {index + 1}
                          </div>
                          <span className="text-white font-medium">
                            Referral {index + 1}
                          </span>
                        </div>
                        
                        {/* Status */}
                        <div className="flex items-center gap-1">
                          {hasReachedThreshold ? (
                            <div className="flex items-center gap-1 text-[#60FF8A]">
                              <div className="w-2 h-2 bg-[#60FF8A] rounded-full"></div>
                              <span className="text-xs font-medium">Active</span>
                            </div>
                          ) : userPoints > 0 ? (
                            <div className="flex items-center gap-1 text-gray-500">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                              <span className="text-xs">Pending</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-gray-600">
                              <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
                              <span className="text-xs">Inactive</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Address */}
                      <div className="text-gray-400 text-sm font-mono">
                        {user.address.slice(0, 8)}...{user.address.slice(-6)}
                      </div>

                      {/* Bottom Row: Points and Earnings */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-700/50">
                        <div className="text-center">
                          <div className="text-xs text-gray-400 mb-1">Their Points</div>
                          <div className="text-white font-semibold">
                            {userPoints.toFixed(0)} RP
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-xs text-gray-400 mb-1">You Earned</div>
                          {pointsEarned > 0 ? (
                            <div className="text-[#60FF8A] font-bold">
                              +{pointsEarned} RP
                            </div>
                          ) : (
                            <div className="text-gray-500 text-sm">-</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Pagination */}
              {referralsData.count > referralsLimit && (
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-2 mt-6">
                  <motion.div
                    whileHover={referralsPage !== 1 ? { scale: 1.05, y: -2 } : {}}
                    whileTap={referralsPage !== 1 ? { scale: 0.95 } : {}}
                  >
                    <Button
                      onClick={() => setReferralsPage(p => Math.max(1, p - 1))}
                      disabled={referralsPage === 1}
                      className="relative bg-gray-800/50 text-white px-4 py-2 rounded-lg disabled:opacity-50 w-full sm:w-auto hover:bg-gray-700/50 transition-all duration-200 hover:shadow-lg"
                    >
                      <span className="absolute inset-0 bg-white opacity-0 hover:opacity-5 transition-opacity duration-200 rounded-lg" />
                      Previous
                    </Button>
                  </motion.div>
                  <span className="text-gray-400 text-sm flex items-center px-4">
                    Page {referralsPage} of {Math.ceil(referralsData.count / referralsLimit)}
                  </span>
                  <motion.div
                    whileHover={referralsPage < Math.ceil(referralsData.count / referralsLimit) ? { scale: 1.05, y: -2 } : {}}
                    whileTap={referralsPage < Math.ceil(referralsData.count / referralsLimit) ? { scale: 0.95 } : {}}
                  >
                    <Button
                      onClick={() => setReferralsPage(p => p + 1)}
                      disabled={referralsPage >= Math.ceil(referralsData.count / referralsLimit)}
                      className="relative bg-gray-800/50 text-white px-4 py-2 rounded-lg disabled:opacity-50 w-full sm:w-auto hover:bg-gray-700/50 transition-all duration-200 hover:shadow-lg"
                    >
                      <span className="absolute inset-0 bg-white opacity-0 hover:opacity-5 transition-opacity duration-200 rounded-lg" />
                      Next
                    </Button>
                  </motion.div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}